import { __extends, __awaiter, __generator, __decorate, __metadata } from 'tslib';
import { convertToDate, configQuery, interpolate, FeatureEnabledV2, getItemDate, getItemTime, formatDate, formatTime, formatCurrency, MagicConfigMixin, ContentMixin } from '@gig/magic-common';
import { Prop, Watch, Component, Mixins } from 'vue-property-decorator';
import { Currency } from '@gig/magic-sdk/dist/adapter/users/signup';
import { Feature } from '@gig/magic-messages';
import { compactFormatter, getSweepRatio } from '../../utils/helpers.js';
import { namespace } from 'vuex-class';
import { CoreXMediatorType } from '@gig/magic-sdk/dist/adapter/responsiblegaming/createUserTransactionLimit';
import { CoreXOperationType } from '@gig/magic-sdk/dist/adapter/users/getUserTransactionsHistory';
import { isFiatCurrency } from '@gig/magic-sdk/dist/util/balance';
import { store } from '@gig/magic-corex/dist/store';
import { TRANSACTIONS_NAMESPACE } from '@gig/magic-corex/dist/store/modules/transactions';
import { getOldestTransactionDate } from '@gig/magic-corex/dist/utils/transactions';

var FreecoinsTransactionColumn;
(function (FreecoinsTransactionColumn) {
    FreecoinsTransactionColumn["DATE"] = "date";
    FreecoinsTransactionColumn["TIME"] = "time";
    FreecoinsTransactionColumn["FREE_COINS"] = "free_coins";
    FreecoinsTransactionColumn["OPERATION_TYPE"] = "operation_type";
    FreecoinsTransactionColumn["ID"] = "id";
})(FreecoinsTransactionColumn || (FreecoinsTransactionColumn = {}));
var transactions = namespace(TRANSACTIONS_NAMESPACE);
var PaymentHistoryType;
(function (PaymentHistoryType) {
    PaymentHistoryType["WITHDRAWAL"] = "withdrawals";
    PaymentHistoryType["DEPOSIT"] = "deposits";
})(PaymentHistoryType || (PaymentHistoryType = {}));
var TARGET_CURRENCY = Currency.XFC;
var DEFAULT_LOAD_AMOUNT = 10;
/**
 * Fetches and displays the list of free coins transactions
 */
var FreecoinsTransactionsComponent = /** @class */ (function (_super) {
    __extends(FreecoinsTransactionsComponent, _super);
    function FreecoinsTransactionsComponent() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        // Allow template to access this enum
        _this.Column = FreecoinsTransactionColumn;
        _this.multiplier = 1;
        // Standard page size for loading more
        _this.pageSize = _this.typeFilter ? DEFAULT_LOAD_AMOUNT * 2 : DEFAULT_LOAD_AMOUNT;
        /**
         * When enabled it will show the time and date
         * in local time instead of UTC.
         */
        _this.isLocalTime = false;
        _this.scrollToBottomOfList = function () {
            _this.$nextTick(function () {
                var historyElement = _this.$refs.history;
                if (historyElement) {
                    historyElement.scrollIntoView({
                        behavior: 'smooth',
                        block: 'end',
                    });
                }
            });
        };
        return _this;
    }
    Object.defineProperty(FreecoinsTransactionsComponent.prototype, "isMobile", {
        get: function () {
            return typeof window !== 'undefined' && window.innerWidth < 768;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(FreecoinsTransactionsComponent.prototype, "fromDate", {
        get: function () {
            return convertToDate(this.from);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(FreecoinsTransactionsComponent.prototype, "toDate", {
        get: function () {
            return convertToDate(this.to);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(FreecoinsTransactionsComponent.prototype, "shownColumns", {
        get: function () {
            return this.columns
                .split(',')
                .map(function (str) {
                return str.trim();
            });
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(FreecoinsTransactionsComponent.prototype, "hasMoreRows", {
        get: function () {
            var _a;
            return (((_a = this.transactions) === null || _a === void 0 ? void 0 : _a.length) || 0) < (this.total || 0);
        },
        enumerable: false,
        configurable: true
    });
    FreecoinsTransactionsComponent.prototype.getOperationTypeDisplay = function (type) {
        if (type === CoreXOperationType.CREDIT) {
            return this.$t('freecoins-transactions.operation-type.credit');
        }
        if (type === CoreXOperationType.DEBIT) {
            return this.$t('freecoins-transactions.operation-type.debit');
        }
        // Fallback for unknown types
        return type ? '' + type : '-';
    };
    Object.defineProperty(FreecoinsTransactionsComponent.prototype, "shownHistory", {
        get: function () {
            var _this = this;
            var _a, _b;
            return (_b = (_a = this.transactions) === null || _a === void 0 ? void 0 : _a.map) === null || _b === void 0 ? void 0 : _b.call(_a, function (item) {
                var currency = item.Payload.CurrencyCode;
                var amount = item.Payload.Amount;
                var isFiat = isFiatCurrency(currency);
                var multiplier = isFiat ? _this.multiplier : 1;
                var coins = multiplier * amount;
                var coinsFormatted = coins && compactFormatter.format(coins);
                return {
                    key: item.TransactionId ||
                        item.TimestampEpoch + "-" + item.Payload.Amount,
                    id: item.TransactionId,
                    date: new Date(item.TimestampEpoch),
                    coins: coins,
                    coinsFormatted: coinsFormatted,
                    operationType: _this.getOperationTypeDisplay(item.Payload.OperationType),
                    idLabel: _this.getIdLabel(item),
                    icon: 'virtualcoin',
                    currency: item.Payload.CurrencyCode,
                };
            }).filter(function (item) {
                if (item.currency === TARGET_CURRENCY) {
                    return true;
                }
                return false;
            });
        },
        enumerable: false,
        configurable: true
    });
    FreecoinsTransactionsComponent.prototype.onTransactionsUpdate = function () {
        if (!this.transactions || this.transactions.length === 0) {
            this.$emit('update-from-date', null); // Emit null if no transactions
            return;
        }
        var oldestDate = getOldestTransactionDate(this.transactions);
        this.$emit('update-from-date', oldestDate);
    };
    FreecoinsTransactionsComponent.prototype.convertHistoryType = function (type) {
        switch (type) {
            case CoreXOperationType.DEBIT:
                return PaymentHistoryType.WITHDRAWAL;
            case CoreXOperationType.CREDIT:
                return PaymentHistoryType.DEPOSIT;
            default:
                // No other types are documented but just in case we
                // do not want to match these.
                return '';
        }
    };
    FreecoinsTransactionsComponent.prototype.created = function () {
        return __awaiter(this, void 0, void 0, function () {
            var _a;
            var _this = this;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        configQuery('wallet.freecoinsHistoryColumns', // Configuration path for columns
                        function (columns) {
                            if (columns && columns.length > 0) {
                                var allValues_1 = Object.values(FreecoinsTransactionColumn);
                                var areValidColumns = columns.every(function (column) {
                                    return allValues_1.includes(column.trim());
                                });
                                if (areValidColumns) {
                                    _this.columns = columns.join(',');
                                }
                            }
                        });
                        // Initial data fetch
                        this.fetchData();
                        _a = this;
                        return [4 /*yield*/, getSweepRatio()];
                    case 1:
                        _a.multiplier = _b.sent();
                        return [2 /*return*/];
                }
            });
        });
    };
    FreecoinsTransactionsComponent.prototype.getIdLabel = function (item) {
        return interpolate(this.$t('sweepx-transactions.id.header'), {
            id: item.TransactionId,
        });
    };
    FreecoinsTransactionsComponent.prototype.loadMore = function () {
        this.pageSize += this.typeFilter
            ? DEFAULT_LOAD_AMOUNT * 2
            : DEFAULT_LOAD_AMOUNT;
        this.fetchData({
            loadMore: true,
            size: DEFAULT_LOAD_AMOUNT,
        });
    };
    FreecoinsTransactionsComponent.prototype.isColumnShown = function (column) {
        return this.shownColumns.includes(column);
    };
    FreecoinsTransactionsComponent.prototype.fetchData = function (_a) {
        var _b;
        var _c = _a === void 0 ? {} : _a, _d = _c.loadMore, loadMore = _d === void 0 ? false : _d, _e = _c.size, size = _e === void 0 ? this.pageSize : _e;
        return __awaiter(this, void 0, void 0, function () {
            var filteredTransactions, e_1;
            return __generator(this, function (_f) {
                switch (_f.label) {
                    case 0:
                        if (!this.from || !this.to) {
                            return [2 /*return*/];
                        }
                        _f.label = 1;
                    case 1:
                        _f.trys.push([1, 5, , 6]);
                        this.onContentLoadInit();
                        return [4 /*yield*/, this.fetchAllTransactions(loadMore, size)];
                    case 2:
                        _f.sent();
                        filteredTransactions = (_b = this.transactions) === null || _b === void 0 ? void 0 : _b.filter(function (item) {
                            return item.Payload.CurrencyCode === TARGET_CURRENCY;
                        });
                        if (!(!(filteredTransactions === null || filteredTransactions === void 0 ? void 0 : filteredTransactions.length) && this.hasMoreRows)) return [3 /*break*/, 4];
                        return [4 /*yield*/, this.fetchAllTransactions(loadMore, size)];
                    case 3:
                        _f.sent();
                        _f.label = 4;
                    case 4:
                        this.onContentLoad(this.transactions);
                        if (loadMore) {
                            this.scrollToBottomOfList();
                        }
                        return [3 /*break*/, 6];
                    case 5:
                        e_1 = _f.sent();
                        this.onContentLoadError(e_1);
                        return [3 /*break*/, 6];
                    case 6: return [2 /*return*/];
                }
            });
        });
    };
    FreecoinsTransactionsComponent.prototype.fetchAllTransactions = function (loadMore, size) {
        var _a;
        return __awaiter(this, void 0, void 0, function () {
            var starting;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        starting = loadMore ? ((_a = this.transactions) === null || _a === void 0 ? void 0 : _a.length) || 0 : 0;
                        return [4 /*yield*/, this.fetchTransactions({
                                from: this.fromDate,
                                to: this.toDate,
                                size: size,
                                starting: starting,
                                mediatorType: CoreXMediatorType.MONETARY_ADJUSTMENT,
                                loadMore: loadMore,
                            })];
                    case 1:
                        _b.sent();
                        return [2 /*return*/, starting];
                }
            });
        });
    };
    __decorate([
        Prop({
            type: String,
            default: FreecoinsTransactionColumn.DATE + "," + FreecoinsTransactionColumn.TIME + "," + FreecoinsTransactionColumn.FREE_COINS + "," + FreecoinsTransactionColumn.OPERATION_TYPE + "," + FreecoinsTransactionColumn.ID,
        }),
        __metadata("design:type", String)
    ], FreecoinsTransactionsComponent.prototype, "columns", void 0);
    __decorate([
        Prop({
            type: [Number, String, Date],
        }),
        __metadata("design:type", Object)
    ], FreecoinsTransactionsComponent.prototype, "from", void 0);
    __decorate([
        Prop({
            type: [Number, String, Date],
        }),
        __metadata("design:type", Object)
    ], FreecoinsTransactionsComponent.prototype, "to", void 0);
    __decorate([
        Prop({
            type: String,
            validator: function (types) {
                if (!types) {
                    return true;
                }
                var found = types.split(',');
                var allValues = Object.values(PaymentHistoryType);
                return found.every(function (type) {
                    return allValues.includes(type.trim());
                });
            },
            default: '',
        }),
        __metadata("design:type", String)
    ], FreecoinsTransactionsComponent.prototype, "typeFilter", void 0);
    __decorate([
        FeatureEnabledV2({
            slug: Feature.TRANSACTIONS_HISTORY_ON_LOCAL_TIME,
        }),
        __metadata("design:type", Boolean)
    ], FreecoinsTransactionsComponent.prototype, "isLocalTime", void 0);
    __decorate([
        transactions.Getter('isLoadingState'),
        __metadata("design:type", void 0)
    ], FreecoinsTransactionsComponent.prototype, "isLoading", void 0);
    __decorate([
        transactions.Getter('getTransactions'),
        __metadata("design:type", void 0)
    ], FreecoinsTransactionsComponent.prototype, "transactions", void 0);
    __decorate([
        transactions.Getter('getTransactionsTotal'),
        __metadata("design:type", void 0)
    ], FreecoinsTransactionsComponent.prototype, "total", void 0);
    __decorate([
        transactions.Action('fetchTransactions'),
        __metadata("design:type", Object)
    ], FreecoinsTransactionsComponent.prototype, "fetchTransactions", void 0);
    __decorate([
        Watch('transactions'),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", []),
        __metadata("design:returntype", void 0)
    ], FreecoinsTransactionsComponent.prototype, "onTransactionsUpdate", null);
    __decorate([
        Watch('from'),
        Watch('to'),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Object]),
        __metadata("design:returntype", Promise)
    ], FreecoinsTransactionsComponent.prototype, "fetchData", null);
    FreecoinsTransactionsComponent = __decorate([
        Component({
            name: 'sweepstakes-freecoins-transactions',
            methods: { getItemDate: getItemDate, getItemTime: getItemTime },
            store: store,
            filters: {
                formatDate: formatDate,
                formatTime: formatTime,
                formatCurrency: formatCurrency,
            },
        })
    ], FreecoinsTransactionsComponent);
    return FreecoinsTransactionsComponent;
}(Mixins(MagicConfigMixin, ContentMixin)));

export default FreecoinsTransactionsComponent;
export { FreecoinsTransactionColumn, PaymentHistoryType, TARGET_CURRENCY };
